import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, ChevronRight, Loader2, Mail, MoreHorizontal, Phone, Plus, RefreshCw, Ship } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CompanyLogo } from "@/components/common";
import { CaseDetails, periodText } from "@/components/canvas/KravFacts";
import { KravDocs } from "@/components/canvas/KravDocs";
import { BasisMark } from "@/components/canvas/BasisMark";
import { DocIcon } from "@/components/canvas/LosBits";
import { ItemInspector } from "@/components/canvas/ItemInspector";
import { CaseTeam } from "@/components/canvas/CaseTeam";
import { DraftLine, IntakeBrief } from "@/components/canvas/OversiktBrief";
import { OversiktStatus } from "@/components/canvas/OversiktStatus";
import { docTitle } from "@/components/canvas/DocViewer";
import { metaItems, rowSummary, workItems, KIND_LABEL, itemKind } from "@/lib/krav";
import { basisOf, daysUntil, longDate, nextAction, phaseLabel, DOC_ROLE } from "@/lib/sk";

const menuItem = "h-9 cursor-pointer gap-2.5 rounded-lg px-2 text-[13px] [&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-muted-foreground";
const go = (tab, detail) => window.dispatchEvent(new CustomEvent("canvas:goto", { detail: { tab, ...detail } }));
const link = "inline-flex items-center gap-1 rounded-sm text-[13.5px] font-medium text-[hsl(var(--brand-strong))] underline-offset-[3px] hover:underline focus-visible:ring-2 focus-visible:ring-ring/40";
const empty = <span className="text-muted-foreground/55">ikke oppgitt</span>;

function Sec({ label, children, testId }) {
  return (
    <section className="border-t border-black/[0.06] pt-7" data-testid={testId}>
      <h3 className="text-[11.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground/80">{label}</h3>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Row({ label, children, testId }) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-x-6 py-3 text-[14.5px] [&+&]:border-t [&+&]:border-black/[0.05] sm:grid-cols-[210px_minmax(0,1fr)]" data-testid={testId}>
      <div className="text-muted-foreground">{label}</div>
      <div className="min-w-0 text-foreground/90">{children}</div>
    </div>
  );
}

/** «Neste …» – one human sentence about where the work stands and the one link that takes Egil there. */
function NextLine({ project, ctx }) {
  const t = ctx.totals || {};
  // Questions people must answer count as clarifications; the configuration's engineer reviews are a technical gate with their own home in Ventiler.
  const isAuto = (q) => (q.origin || {}).type === "konfigurasjon";
  const allQ = (ctx.items || []).flatMap((i) => [i, ...(i.children || [])].flatMap((x) => (x.clarifications || []).filter((q) => q.status !== "besvart" && q.status !== "lukket")));
  const open = allQ.filter((q) => !isAuto(q)).length;
  const eng = allQ.length - open;
  const engValves = ctx.konfig?.active?.valve_status?.krever_ingenior || 0;
  const a0 = nextAction({ id: project.id, status: project.status, calc: t, quote: ctx.quote, open_clarifications: open, reading: project.reading?.status, tender_deadline: project.facts?.tender_deadline, risk: ctx.risiko || null });
  const a = a0?.key === "price" && eng ? { key: "engineer", label: `${eng} ${eng === 1 ? "ingeniørvurdering" : "ingeniørvurderinger"}`, tab: "ventiler" } : a0;
  if (!a || a.key === "confirm" || a.key === "restore") return null;
  const left = Math.max(0, (t.coverage?.total || 0) - (t.coverage?.priced || 0));
  const engText = engValves ? `${engValves} ${engValves === 1 ? "ventil krever" : "ventiler krever"} ingeniørvurdering` : `${eng} ${eng === 1 ? "ingeniørvurdering venter" : "ingeniørvurderinger venter"}`;
  const text = {
    reading: "Dokumentene leses. Omfang og fakta kommer inn etter hvert.",
    clarify: `${open} ${open === 1 ? "avklaring må håndteres" : "avklaringer må håndteres"} før kalkylen kan ferdigstilles.`,
    engineer: `${engText} før leveransen er teknisk klar – enkeltvirkende aktuatorer godkjennes aldri automatisk.`,
    attention: `${t.attention} ${t.attention === 1 ? "linje i kalkylen må vurderes" : "linjer i kalkylen må vurderes"}.`,
    items: "Ingen leveranse ennå. Last opp ventillisten og bekreft prosjekttrykket, så bygges leveransegruppene.",
    price: t.sum_kind === "none" ? "Omfanget er på plass. Kalkylen kan starte." : `${left} ${left === 1 ? "post gjenstår" : "poster gjenstår"} å prise.`,
    revision: ctx.quote?.stale ? "Kalkylen er oppdatert etter siste versjon. Tilbudet bør oppdateres." : "Kalkylen er ferdig. Tilbudet kan gjennomgås.",
    risk: "Tilbudet krever risikovurdering før det kan kontrolleres.",
    control: `Versjon ${ctx.quote?.revision} er klar til kontroll.`,
    ready: "Tilbudet er kontrollert. Sending er ikke bygget.",
  }[a.key] || a.label;
  const action = { clarify: ["Besvar avklaringer", "krav", { filter: "oppfolging" }], engineer: ["Åpne Ventiler", "ventiler", { filter: "krever_ingenior" }], attention: ["Åpne kalkylen", "kalkyle"], items: ["Åpne omfanget", "krav"], price: [t.sum_kind === "none" ? "Start kalkylen" : "Åpne kalkylen", "kalkyle"], revision: ["Åpne tilbud", "tilbud"], risk: ["Åpne tilbud", "tilbud"], control: ["Åpne tilbud", "tilbud"], ready: ["Åpne tilbud", "tilbud"] }[a.key];
  const done = a.key === "ready";
  return (
    <div className={cn("mt-7 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[12px] px-4 py-3 text-[14.5px]", done ? "bg-[hsl(var(--v-ok-bg))]" : "bg-[hsl(var(--brand)/0.045)]")} data-testid="oversikt-next" data-action={a.key}>
      {a.key === "reading" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : done ? <Check className="h-3.5 w-3.5 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> : <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--brand-strong))]">Neste</span>}
      <span className="min-w-0 flex-1 text-foreground/90">{text}</span>
      {action ? <button type="button" onClick={() => go(action[1], action[2])} className={link} data-testid="oversikt-next-action">{action[0]} <ArrowRight className="h-3.5 w-3.5" /></button> : null}
    </div>
  );
}

/** One document in the list: a readable title, role and size on the second line. Click opens it beside the work. */
function DocRow({ d, onOpen }) {
  const meta = [DOC_ROLE[d.role]?.label, d.pages ? `${d.pages} ${d.pages === 1 ? "side" : "sider"}` : null, d.parsed?.jobs ? `${d.parsed.jobs} arbeidsposter` : null].filter(Boolean).join(" · ");
  return (
    <li>
      <button type="button" onClick={() => onOpen(d.id)} className="group flex w-full items-center gap-3.5 rounded-[10px] px-2.5 py-2.5 text-left transition-colors hover:bg-black/[0.03]" title={d.name} data-testid={`oversikt-doc-${d.id}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[hsl(210_20%_96%)] text-muted-foreground transition-colors group-hover:bg-[hsl(var(--brand-soft))] group-hover:text-primary"><DocIcon kind={d.kind} className="h-4 w-4 text-current" /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14.5px] font-medium text-foreground/90">{docTitle(d)}</span>
          {meta ? <span className="block truncate text-[12.5px] text-muted-foreground">{meta}</span> : null}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
    </li>
  );
}

/**
 * Oversikt: the work as a whole, readable in ten seconds – who, what, when, what the request itself says, what needs
 * follow-up and which documents it rests on. Calm and textual; the jobs live under Arbeidsomfang.
 */
export function OversiktTab({ ctx, project, ops, busy, draftOps }) {
  const [more, setMore] = useState(false);
  const [allDocs, setAllDocs] = useState(false);
  const [restructuring, setRestructuring] = useState(false);
  const [openMeta, setOpenMeta] = useState(null);
  const f = project.facts || {};
  const contact = project.contact || {};
  const all = useMemo(() => ctx.items || [], [ctx.items]);
  const items = useMemo(() => workItems(all), [all]);
  const meta = useMemo(() => metaItems(all), [all]);
  const docs = useMemo(() => (ctx.documents || []).filter((d) => d.status !== "duplikat"), [ctx.documents]);
  const parsedDocs = docs.filter((d) => d.parsed).length;
  const clarifications = useMemo(() => items.flatMap((i) => [i, ...(i.children || [])].flatMap((x) => (x.clarifications || []).filter((q) => q.status !== "besvart").map((q) => ({ item: x, q })))), [items]);
  const summary = project.summary || "";
  const long = summary.length > 300;
  const period = periodText(f.period_from, f.period_to);
  const days = daysUntil(f.tender_deadline);
  const confirm = (...keys) => ops.patchCase({ confirm: keys }, "Bekreftet");
  const restructure = async () => { setRestructuring(true); try { await ops.restructure(); } finally { setRestructuring(false); } };
  const openItem = openMeta ? all.find((i) => i.id === openMeta) : null;

  // A customer without a known website gets one looked up (verified on the web) so the real logo can show – once, quietly.
  const looked = useRef(null);
  useEffect(() => {
    const cust = project.customer || {};
    if (!cust.name || cust.website || cust.logo_lookup_at || looked.current === project.id) return;
    looked.current = project.id;
    api.post(`/projects/${project.id}/customer/logo`).then((r) => { if (r.data.website) ops.apply({ project: { ...project, customer: r.data.customer } }); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, project.customer?.website]);

  return (
    <div className="mx-auto w-full max-w-[860px] px-5 pb-20 pt-10 sm:px-10" data-testid="oversikt-tab">
      <DraftLine project={project} draftOps={draftOps} />
      <div className="flex items-start gap-5" data-testid="oversikt-hero">
        <CompanyLogo name={project.customer?.name || project.name} domain={project.customer?.website} size="xl" className="mt-0.5 shadow-[0_1px_2px_hsl(212_40%_16%/0.06),0_10px_30px_-18px_hsl(212_40%_16%/0.35)]" testId="oversikt-logo" />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[26px] font-semibold leading-[1.15] tracking-[-0.026em] text-[hsl(var(--navy))]" data-testid="oversikt-header">{project.name || "Ny sak"}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[14.5px] text-muted-foreground" data-testid="oversikt-subtitle">
            {project.customer?.name ? <span className="font-medium text-foreground/80">{project.customer.name}</span> : null}
            {project.vessel?.name ? <><span className="text-muted-foreground/40">·</span><span className="inline-flex items-center gap-1.5"><Ship className="h-3.5 w-3.5 text-muted-foreground/70" strokeWidth={1.8} />{project.vessel.name}</span></> : null}
            <span className="text-muted-foreground/40">·</span>
            <span data-testid="oversikt-status">{phaseLabel(project, ctx)}</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground data-[state=open]:bg-black/[0.06]" aria-label="Flere valg" data-testid="oversikt-menu"><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={6} className="w-64 rounded-xl border-black/[0.06] p-1.5 shadow-float">
            {parsedDocs ? <DropdownMenuItem disabled={busy || restructuring} onSelect={restructure} className={menuItem} data-testid="oversikt-restructure"><RefreshCw className={restructuring ? "animate-spin" : ""} /> Analyser dokumentene på nytt</DropdownMenuItem> : null}
            <DropdownMenuItem onSelect={() => go("krav", { add: true })} className={menuItem} data-testid="oversikt-new-item"><Plus /> Ny arbeidspost</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CaseTeam ctx={ctx} project={project} ops={ops} />

      <div className="mt-8 max-w-[680px]">
        {summary ? (
          <div data-testid="oversikt-summary">
            <p className={cn("text-[16px] leading-[1.7] text-foreground/90", !more && long && "line-clamp-4")}>{summary}</p>
            {long ? <button type="button" onClick={() => setMore((v) => !v)} aria-expanded={more} className="mt-1.5 text-[13.5px] font-medium text-[hsl(var(--brand-strong))] underline-offset-[3px] hover:underline" data-testid="oversikt-summary-toggle">{more ? "Vis mindre" : "Vis hele sammendraget"}</button> : null}
          </div>
        ) : <p className="text-[16px] leading-[1.7] text-muted-foreground" data-testid="oversikt-summary">Ingen beskrivelse ennå. Slipp forespørselen i assistenten – sammendrag, frister og arbeidsomfang hentes fra dokumentene.</p>}
      </div>
      <NextLine project={project} ctx={ctx} />
      <OversiktStatus ctx={ctx} project={project} />
      <IntakeBrief project={project} onGo={(tab) => go(tab)} />

      <div className="mt-10 space-y-9">
        <Sec label="Viktig" testId="oversikt-key">
          <Row label="Tilbudsfrist" testId="oversikt-deadline">
            {f.tender_deadline ? <>{longDate(f.tender_deadline)}{days !== null ? <span className={cn("ml-2 text-[13px]", days < 0 ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground")}>{days < 0 ? "passert" : days === 0 ? "i dag" : `om ${days} ${days === 1 ? "dag" : "dager"}`}</span> : null}<BasisMark basis={basisOf(project, "tender_deadline")} onConfirm={() => confirm("tender_deadline")} testId="oversikt-deadline-basis" compact hideConfirmed className="ml-2" /></> : empty}
          </Row>
          <Row label="Ønsket levering" testId="oversikt-period">
            {period ? <>{period}{f.commissioning_days ? <span className="text-muted-foreground"> · {f.commissioning_days} dager igangkjøring</span> : null}<BasisMark basis={basisOf(project, "period_from") || basisOf(project, "period_to")} onConfirm={() => confirm("period_from", "period_to")} testId="oversikt-period-basis" compact hideConfirmed className="ml-2" /></> : empty}
          </Row>
          <Row label="Kontaktperson" testId="oversikt-contact">
            {contact.name ? (
              <div className="min-w-0">
                <div>{contact.name}{contact.role ? <span className="text-muted-foreground"> · {contact.role}</span> : null}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[13px] text-muted-foreground">
                  {contact.email ? <a href={`mailto:${contact.email}`} className="inline-flex min-w-0 items-center gap-1.5 truncate transition-colors hover:text-foreground"><Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} /><span className="truncate">{contact.email}</span></a> : null}
                  {contact.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" strokeWidth={1.8} />{contact.phone}</span> : null}
                </div>
              </div>
            ) : empty}
          </Row>
        </Sec>

        {meta.length ? (
          <Sec label="Fra forespørselen" testId="oversikt-request">
            <p className="text-[13.5px] text-muted-foreground">Innledning, konkurransekrav, vilkår og budsjettlinjer fra kundens dokument – ikke arbeid på fartøyet.</p>
            <ul className="-mx-2.5 mt-3">
              {meta.map((it) => {
                const s = rowSummary(it, 150);
                return (
                  <li key={it.id}>
                    <button type="button" onClick={() => setOpenMeta(it.id)} className="group flex w-full items-start gap-3.5 rounded-[10px] px-2.5 py-2.5 text-left transition-colors hover:bg-black/[0.03]" data-testid={`oversikt-meta-${it.ref}`}>
                      <span className="mt-[3px] shrink-0 font-mono text-[12.5px] text-muted-foreground">{it.ref}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2"><span className="truncate text-[14.5px] font-medium text-foreground/90">{it.title}</span><span className="shrink-0 text-[12px] text-muted-foreground/70">{KIND_LABEL[itemKind(it)]}</span></span>
                        {s.text ? <span className="mt-0.5 line-clamp-2 block text-[13.5px] leading-[1.5] text-foreground/65">{s.text}</span> : null}
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </Sec>
        ) : null}

        {clarifications.length ? (
          <Sec label="Krever oppfølging" testId="oversikt-followup">
            <div className="text-[14.5px] font-medium">{clarifications.length} {clarifications.length === 1 ? "åpen avklaring" : "åpne avklaringer"}</div>
            <ul className="mt-2.5 space-y-2">
              {clarifications.slice(0, 6).map(({ item, q }) => (
                <li key={q.id} className="flex items-start gap-3 text-[14.5px] leading-[1.55]" data-testid={`oversikt-clarification-${q.id}`}>
                  <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--v-assumed))]" />
                  <button type="button" onClick={() => go("krav", { ref: item.ref })} className="min-w-0 text-left transition-colors hover:text-[hsl(var(--brand-strong))]"><span className="font-mono text-[12.5px] text-muted-foreground">{item.ref}</span> <span className="text-foreground/90">{q.text}</span></button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => go("krav", { filter: "oppfolging" })} className={cn(link, "mt-4")} data-testid="oversikt-see-clarifications">Se avklaringer <ArrowRight className="h-3.5 w-3.5" /></button>
          </Sec>
        ) : null}

        <Sec label="Dokumenter" testId="oversikt-docs">
          {docs.length ? (
            <>
              {!allDocs ? <ul className="-mx-2.5">{docs.slice(0, 6).map((d) => <DocRow key={d.id} d={d} onOpen={(id) => ops.openDoc(id)} />)}</ul> : null}
              {docs.length > 6 || allDocs ? <button type="button" onClick={() => setAllDocs((v) => !v)} aria-expanded={allDocs} className={cn(link, "mt-3")} data-testid="oversikt-docs-toggle">{allDocs ? "Vis færre" : `Vis alle ${docs.length}`}</button> : null}
              {allDocs ? <div className="mt-3"><KravDocs ctx={ctx} ops={ops} /></div> : null}
            </>
          ) : <p className="text-[14.5px] text-muted-foreground">Ingen dokumenter på saken. Slipp filer i assistenten.</p>}
        </Sec>

        <CaseDetails project={project} ctx={ctx} ops={ops} />
      </div>
      {openItem ? <ItemInspector item={openItem} ctx={ctx} project={project} ops={ops} mode="info" testId="oversikt-inspector" onClose={() => setOpenMeta(null)} /> : null}
    </div>
  );
}
